import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Truck, Clock, ExternalLink, RefreshCw, ShoppingCart } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface UserOrder {
  id: string;
  displayOrderNumber: string;
  status: string;
  totalAmount: string;
  createdAt: string;
  trackingNumber: string | null;
  shippingCarrier: string | null;
}

const statusLabels: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  PENDING: { label: "Pending", variant: "secondary" },
  CONFIRMED: { label: "Confirmed", variant: "default" },
  PROCESSING: { label: "Processing", variant: "default" },
  PACKED: { label: "Packed", variant: "default" },
  SHIPPED: { label: "Shipped", variant: "default" },
  DELIVERED: { label: "Delivered", variant: "default" },
  CANCELLED: { label: "Cancelled", variant: "destructive" },
  REFUNDED: { label: "Refunded", variant: "outline" },
  READY_FOR_PICKUP: { label: "Ready for Pickup", variant: "default" },
  PICKED_UP: { label: "Picked Up", variant: "default" },
};

const carrierTrackingUrls: Record<string, string> = {
  UPS: "https://www.ups.com/track?tracknum=",
  FEDEX: "https://www.fedex.com/fedextrack/?tracknumbers=",
  USPS: "https://tools.usps.com/go/TrackConfirmAction?tLabels=",
  DHL: "https://www.dhl.com/en/express/tracking.html?AWB=",
  ESTES: "https://www.estes-express.com/myestes/tracking/trackingResults/",
  ABF: "https://arcb.com/tools/tracking.html#",
  SAIA: "https://www.saia.com/track/details?pro=",
  OLD_DOMINION: "https://www.odfl.com/Trace/standardResult.faces?pro=",
  XPO: "https://www.xpo.com/track/",
  SOUTHEASTERN: "https://www.sefl.com/trace",
  DAYTON: "https://www.daytonfreight.com/track?pro=",
  ROADRUNNER: "https://my.rrts.com/shipment-tracking?pro=",
  YRCFREIGHT: "https://my.yrc.com/tools/track/shipments?searchText=",
  REDDAWAY: "https://reddaway.com/trace?pro=",
};

interface ReorderResponse {
  success: boolean;
  addedCount: number;
  totalItems: number;
  unavailableItems: string[];
  message: string;
}

export function CustomerOrdersSection() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  
  const { data: orders, isLoading } = useQuery<UserOrder[]>({
    queryKey: ["/api/my/orders"],
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await apiRequest("POST", `/api/my/orders/${orderId}/reorder`);
      return response.json() as Promise<ReorderResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/cart"] });
      
      if (data.addedCount > 0) {
        toast({
          title: "Items added to cart",
          description: data.unavailableItems.length > 0 
            ? `${data.addedCount} item(s) added. ${data.unavailableItems.length} item(s) unavailable.`
            : data.message,
        });
        navigate("/cart");
      } else {
        toast({
          title: "Unable to reorder",
          description: data.unavailableItems.join(", ") || "No items could be added to cart",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add items to cart",
        variant: "destructive",
      });
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num / 100);
  };

  const getTrackingUrl = (carrier: string | null, trackingNumber: string | null): string | null => {
    if (!carrier || !trackingNumber) return null;
    const baseUrl = carrierTrackingUrls[carrier.toUpperCase()];
    return baseUrl ? baseUrl + encodeURIComponent(trackingNumber) : null;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse h-32 bg-gray-200 rounded"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-foreground">Order History</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">View and track your orders</p>
      </div>

      {!orders || orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No orders yet</h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-sm">
              When you place an order, it will appear here so you can track its status.
            </p>
            <Link href="/">
              <Button data-testid="button-browse-products">Browse Products</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const statusInfo = statusLabels[order.status] || { label: order.status, variant: "secondary" as const };
            const trackingUrl = getTrackingUrl(order.shippingCarrier, order.trackingNumber);
            
            return (
              <Card key={order.id} data-testid={`card-order-${order.id}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-lg" data-testid={`text-order-number-${order.id}`}>
                          Order #{order.displayOrderNumber}
                        </span>
                        <Badge variant={statusInfo.variant} data-testid={`badge-order-status-${order.id}`}>
                          {statusInfo.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(order.createdAt)}
                        </span>
                        <span className="font-medium text-foreground" data-testid={`text-order-total-${order.id}`}>
                          {formatCurrency(order.totalAmount)}
                        </span>
                      </div>
                      
                      {order.trackingNumber && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Tracking Info</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {order.shippingCarrier && (
                              <Badge variant="outline">{order.shippingCarrier}</Badge>
                            )}
                            <span className="text-sm font-mono" data-testid={`text-tracking-${order.id}`}>
                              {order.trackingNumber}
                            </span>
                            {trackingUrl && (
                              <a 
                                href={trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                data-testid={`link-track-${order.id}`}
                              >
                                Track Package
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 sm:items-end shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reorderMutation.mutate(order.id)}
                        disabled={reorderMutation.isPending}
                        data-testid={`button-reorder-${order.id}`}
                      >
                        {reorderMutation.isPending ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ShoppingCart className="h-4 w-4 mr-2" />
                        )}
                        Buy Again
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
